/* eslint-disable max-lines */
import type React from "react";
import { useEffect, useState, useRef, useCallback } from "react";

import { colorsSelector } from "@redux/colorPalette/selectors";
import { setIsWaitingSongActivated } from "@redux/featureFlags";
import { switchInterfaceLanguage } from "@redux/language";
import { playNextSong } from "@redux/player";
import { roomIdSelector } from "@redux/room/selectors";
import useSelector from "@redux/useSelector";
import { setUserUuid } from "@redux/user";
import { userTypeSelector } from "@redux/user/selectors";
import { vocalGuideRequestSelector } from "@redux/videoPlayerResources/selectors";
import { RequestState } from "@redux/videoPlayerResources/types";
import * as Sentry from "@sentry/browser";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import type { Song, Playlist } from "src/@types/api-types";

import { TVMessage } from "components/b2b/WaitingList/components/LastSong/components/TVMessage/TVMessage";
import { manualTimeUpdateEvent } from "components/b2c/Mp3KbpPlayer/Mp3KbpPlayer.constants";
import Camera from "components/features/Camera/Camera";
import PlayerAreaEventsWrapper from "components/shared/PlayerAreaEventsWrapper";
import { PATHS } from "constants/pathsB2b";
import { sendMultiScreenEventToIpad } from "services/helpers/multiScreen";
import { useIpadToTvMultiScreenEventsListener } from "services/hooks/useMultiScreenEventListener";
import { useVocalGuideSongEndGuard } from "services/hooks/useSongEndGuard";
import { useVocalGuideState } from "services/hooks/useVocalGuideState";
import { SCOPE } from "shared-types/constants";
import { PlayerEvent } from "shared-types/player-event";
import { getUserContext } from "shared-types/user";

import { PlaceholderLogoStrategy } from "./B2bPlayerArea.strategy";
import { B2bPlaceholder } from "./B2bPlayerArea.style";
import { GameIdentifier, type Game } from "shared-types/games";
import { TVRoulette } from "../Roulette/RouletteTV";

import type { PartialSong } from "@redux/player/types";
import { CustomerVideo } from "../CustomerVideo/CustomerVideo";
import { GameDisplayModal } from "../Games/GameDisplayModal";

const B2bPlayerArea: React.FunctionComponent = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [currentSong, setCurrentSong] = useState<{ id: string } | null>(null);
	const [waitingList, setWaitingList] = useState<Song[]>([]);
	const [isStorageActivated, setIsStorageActivated] = useState<boolean>(false);
	const [currentGame, setCurrentGame] = useState<Game | null>(null);
	const [isGameLoading, setIsGameLoading] = useState<boolean>(false);
	const [launchRoulette, setLaunchRoulette] = useState<{
		players: string[];
		song: PartialSong;
		selectedPlayer: number;
		playlist: Playlist;
	} | null>(null);
	const [customerVideo, setCustomerVideo] = useState<string | null>(null);
	const [instrumentalVideoElement, setInstrumentalVideoElement] =
		useState<HTMLVideoElement | null>(null);
	const [vocalGuideVideoElement, setVocalGuideVideoElement] =
		useState<HTMLVideoElement | null>(null);
	// this ref is only here to enable defining a fixed handlePlay callback
	// to prevent triggering play from Mp3KbpPlayer when switching vocal guide state
	// To access the activeVideoElement, use getVideoElementsByStatus()
	const activeVideoElementRef = useRef<HTMLVideoElement | null>(
		instrumentalVideoElement,
	);
	const vocalGuideRequest = useSelector(vocalGuideRequestSelector);
	const userType = useSelector(userTypeSelector);
	const roomId = useSelector(roomIdSelector);
	const {
		isVocalGuideActivated,
		activeVideoElement,
		activateVocalGuide,
		deactivateVocalGuide,
	} = useVocalGuideState(instrumentalVideoElement, vocalGuideVideoElement);

	const cachedVolume = useRef<number>(1);

	useVocalGuideSongEndGuard(activeVideoElement);

	const colors = useSelector(colorsSelector);

	useEffect(() => {
		if (vocalGuideRequest.state === RequestState.success) {
			sendMultiScreenEventToIpad(PlayerEvent.ON_VOCAL_GUIDE_LOADED);
		}
	}, [vocalGuideRequest.state]);

	useEffect(() => {
		activeVideoElementRef.current = activeVideoElement;
		activeVideoElementRef.current?.play();
	}, [activeVideoElement?.src]);

	useEffect(() => {
		sendMultiScreenEventToIpad(
			isVocalGuideActivated
				? PlayerEvent.ON_UNMUTE_VOCAL_GUIDE
				: PlayerEvent.ON_MUTE_VOCAL_GUIDE,
		);
	}, [isVocalGuideActivated]);

	useIpadToTvMultiScreenEventsListener({
		[PlayerEvent.SELECT_SONG]: (event: MessageEvent) => {
			if (activeVideoElementRef.current) {
				activeVideoElementRef.current.pause();
				activeVideoElementRef.current.currentTime = 0;
			}
			setCurrentSong(event.data ? { id: event.data } : null);
		},
		[PlayerEvent.END_SESSION]: () => {
			navigate(PATHS.PLAYER_SESSION_END);
		},
		[PlayerEvent.MUTE_VOCAL_GUIDE]: () => {
			deactivateVocalGuide();
		},
		[PlayerEvent.UNMUTE_VOCAL_GUIDE]: () => {
			activateVocalGuide();
		},
		[PlayerEvent.UPDATE_VOLUME_LEVEL]: (event: MessageEvent) => {
			updateVolumeLevel(event.data);
		},
		[PlayerEvent.SYNCHRONIZE_USER_ID_ACCROSS_SCREEN]: (event: MessageEvent) => {
			dispatch(setUserUuid(event.data));
		},
		[PlayerEvent.WAITING_LIST_UPDATE]: (event: MessageEvent) => {
			try {
				const updatedWaintingList = JSON.parse(event.data);
				setWaitingList(updatedWaintingList);
			} catch (error) {
				Sentry.captureException(
					new Error(
						`Error updating the playlist waiting list ${error.message}, event data ${event.data}`,
					),
					{
						tags: {
							roomId,
						},
					},
				);
			}
		},
		[PlayerEvent.IS_STORAGE_ACTIVATED]: (event: MessageEvent) => {
			const isStorageActivated: boolean = JSON.parse(event.data);
			setIsStorageActivated(isStorageActivated);
		},
		[PlayerEvent.IS_WAITING_SONG_ACTIVATED]: (event: MessageEvent) => {
			const isActivated = JSON.parse(event.data);
			dispatch(setIsWaitingSongActivated(isActivated));
		},
		[PlayerEvent.SWITCH_LANGUAGE]: (event: MessageEvent) => {
			const language = JSON.parse(event.data);
			dispatch(switchInterfaceLanguage(language));
		},
		[PlayerEvent.START_GAME]: (event: MessageEvent) => {
			const game = JSON.parse(event.data);
			setCurrentSong(null);
			setCurrentGame(game);
			setIsGameLoading(true);
		},
		[PlayerEvent.CLOSE_GAME]: () => {
			setCurrentGame(null);
			setLaunchRoulette(null);
		},
		[PlayerEvent.PLAY_ROULETTE]: (event: MessageEvent) => {
			setCurrentSong(null);
			const rouletteData = JSON.parse(event.data);
			if (rouletteData) {
				setLaunchRoulette(rouletteData);
			} else {
				setLaunchRoulette(null);
			}
		},
		[PlayerEvent.PLAY_CUSTOMER_VIDEO]: (event: MessageEvent) => {
			setCurrentSong(null);
			const customerVideo = event.data;
			if (customerVideo) {
				setCustomerVideo(customerVideo);
			}
		},
	});

	const handlePlay = useCallback(() => {
		activeVideoElementRef.current?.play();
	}, []);

	const updateVolumeLevel = (volumeLevel: number) => {
		cachedVolume.current = volumeLevel / 100;

		if (activeVideoElementRef?.current) {
			activeVideoElementRef.current.volume = volumeLevel / 100;
			try {
				const audioContext =
					activeVideoElementRef.current.audioContext || new AudioContext();
				activeVideoElementRef.current.audioContext = audioContext;

				const source =
					activeVideoElementRef.current.sourceNode ||
					audioContext.createMediaElementSource(activeVideoElementRef.current);
				activeVideoElementRef.current.sourceNode = source;

				if (audioContext.state !== "running") {
					audioContext.resume();
				}

				const gainNode =
					activeVideoElementRef.current.gainNode || audioContext.createGain();
				activeVideoElementRef.current.gainNode = gainNode;

				source.disconnect();
				source.connect(gainNode);
				gainNode.connect(audioContext.destination);
				gainNode.gain.value = volumeLevel / 100;
				gainNode.gain.linearRampToValueAtTime(
					volumeLevel / 100,
					audioContext.currentTime + 0.01,
				);
			} catch (error) {
				console.error("AudioContext could not be initialized: ", error);
			}
		}
	};

	useEffect(() => {
		return () => {
			if (activeVideoElementRef.current?.audioContext) {
				activeVideoElementRef.current.audioContext.close();
			}
		};
	}, []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (activeVideoElementRef?.current) {
			activeVideoElementRef.current.volume = cachedVolume.current;
		}
		activeVideoElement?.dispatchEvent(manualTimeUpdateEvent);
	}, [activeVideoElement, cachedVolume]);

	const handleVideoEnd = () => {
		if (currentGame?.identifier === GameIdentifier.ROULETTE) {
			return setLaunchRoulette(null);
		}
		dispatch(playNextSong());
	};

	const handleLaunchRouletteSong = () => {
		sendMultiScreenEventToIpad(PlayerEvent.ROULLETTE_ANIMATION_END);
		activeVideoElement?.play();
		setLaunchRoulette(null);
	};

	return (
		<>
			{customerVideo && (
				<CustomerVideo
					src={customerVideo}
					onVideoEnd={() => setCustomerVideo(null)}
				/>
			)}
			<TVMessage />
			<Camera />
			{currentGame &&
				currentGame?.identifier === GameIdentifier.ROULETTE &&
				!currentSong ? (
				<TVRoulette
					launchRoulette={launchRoulette}
					onAnimationStop={handleLaunchRouletteSong}
				/>
			) : (
				<>
					{!currentSong ? (
						<B2bPlaceholder {...{ colors }}>
							{PlaceholderLogoStrategy(getUserContext(userType))}
						</B2bPlaceholder>
					) : (
						<PlayerAreaEventsWrapper
							song={currentSong}
							activeVideoElement={activeVideoElement}
							setInstrumentalVideoElement={setInstrumentalVideoElement}
							setVocalGuideVideoElement={setVocalGuideVideoElement}
							handlePlay={handlePlay}
							handleVideoEnd={handleVideoEnd}
							controls={false}
							sendPlayerEvent={sendMultiScreenEventToIpad}
							usePlayerEventsListener={useIpadToTvMultiScreenEventsListener}
							scope={SCOPE.B2B}
							waitingList={waitingList}
							isStorageActivated={isStorageActivated}
						/>
					)}
				</>
			)}

			{!!currentGame && (
				<GameDisplayModal
					currentGame={currentGame}
					isGameLoading={isGameLoading}
					setIsGameLoading={setIsGameLoading}
					url={currentGame.displayUrl || currentGame.url || ""}
				/>
			)}
		</>
	);
};

export default B2bPlayerArea;
